import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { AuthContext, apiClient } from '../App';
import PlayerFollowButton from '../components/PlayerFollowButton';
import { Button } from '@/components/ui/button';
import { resolveAvatarUrl } from '../lib/avatarUrl';

const TrainerConnections = ({ direction }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (cursor = null) => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (cursor) params.set('cursor', cursor);
      const response = await apiClient.get(`/hosts/${userId}/${direction}?${params.toString()}`);
      setItems(current => {
        const combined = cursor ? [...current, ...response.data.items] : response.data.items;
        return [...new Map(combined.map(item => [item.id, item])).values()];
      });
      setNextCursor(response.data.next_cursor);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [direction, userId]);

  useEffect(() => { load(0); }, [load]);
  const title = direction === 'followers' ? 'Followers' : 'Following';
  return <main className="min-h-screen bg-zinc-50 px-4 py-8"><div className="mx-auto max-w-lg">
    <div className="mb-6 flex items-center gap-3"><Button type="button" variant="ghost" aria-label="Back to trainer page" onClick={() => navigate(`/host/${userId}`)}><ArrowLeft className="h-5 w-5" /></Button><h1 className="text-2xl font-bold">{title}</h1></div>
    {error ? <div className="rounded-xl bg-white p-8 text-center"><p>Could not load {title.toLowerCase()}.</p><Button className="mt-4" onClick={() => load(0)}>Try again</Button></div> : items.length === 0 && !loading ? <div className="rounded-xl bg-white p-10 text-center text-zinc-600"><Users className="mx-auto mb-3 h-10 w-10 text-zinc-300" />No eligible public {title.toLowerCase()}.</div> : <div className="space-y-2">{items.map(person => <div key={person.id} className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
      {person.avatar_url ? <img src={resolveAvatarUrl(person.avatar_url)} alt="" className="h-11 w-11 rounded-full object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-200 font-semibold">{person.display_name?.charAt(0)}</div>}
      {token ? <button type="button" onClick={() => navigate(`/player/${person.username}`)} className="min-w-0 flex-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"><span className="block truncate font-semibold">{person.display_name}</span>{person.username && <span className="block truncate text-sm text-zinc-500">@{person.username}</span>}</button> : <div className="min-w-0 flex-1"><span className="block truncate font-semibold">{person.display_name}</span>{person.username && <span className="block truncate text-sm text-zinc-500">@{person.username}</span>}</div>}
      {token && user?.id !== person.id && <PlayerFollowButton userId={person.id} username={person.username} isFollowing={person.viewer_is_following} isRequested={person.viewer_request_pending} />}
    </div>)}</div>}
    {loading && <p className="py-6 text-center text-zinc-500" aria-live="polite">Loading…</p>}
    {!loading && nextCursor && !error && <Button type="button" variant="outline" className="mt-5 w-full" onClick={() => load(nextCursor)}>Load more</Button>}
  </div></main>;
};

export default TrainerConnections;
